import React from 'react'
import { Box, Button, Stack, useToast } from '@chakra-ui/react'
import PurpleBubbleContent from '../../../../../components/PurpleBubbleContent'
import usePurpleBubblesText from '../../../../../hooks/usePurpleBubblesText'
import useConfigList from '../../../../../hooks/useConfigList'
import {
  useAmalgamAdjustments,
  useRootCanalAdjustments,
  useImplantAdjustments,
  useCavitationAdjustments,
  useDentalProcedures,
} from '../hooks'

// Import components
import AmalgamsSection from './AmalgamsSection'
import RootCanalsSection from './RootCanalsSection'
import ExtractionsSection from './ExtractionsSection'
import ImplantsSection from './ImplantsSection'
import DentalVisualizations from './DentalVisualizations'

// Import hooks and context
import { useDentalForm } from '../context/DentalFormContext'
import useChartData from '../hooks/useChartData'
import useFormSubmission from '../hooks/useFormSubmission'

interface DentalFormProps {
  uid: string
}

/**
 * DentalForm component that uses context for state management
 */
const DentalForm: React.FC<DentalFormProps> = ({ uid }) => {
  const toast = useToast()
  const purpleBubbleContent = usePurpleBubblesText('Profile-step-5')

  // Get dental procedures and config lists
  const { dentalProcedures, loading: loadingProcedures } = useDentalProcedures()
  const dentalItems = useConfigList('dentalProcedures')

  // Use the form context to access all state and calculations
  const {
    // Amalgam state
    currentNumAmalgamFillings,
    setCurrentNumAmalgamFillings,
    amalgamTotalTeeth,
    setAmalgamTotalTeeth,
    firstAmalgamAge,
    setFirstAmalgamAge,
    lastAmalgamAge,
    setLastAmalgamAge,

    // Root Canal state
    currentRootCanals,
    setCurrentRootCanals,
    rootCanalsTotal,
    setRootCanalsTotal,
    firstRootCanalAge,
    setFirstRootCanalAge,
    lastRootCanalAge,
    setLastRootCanalAge,

    // Extractions/Cavitations state
    removedTeeth,
    setRemovedTeeth,
    firstToothExtractedAge,
    setFirstToothExtractedAge,
    cavitChecked,
    setCavitChecked,
    cavitations,
    setCavitations,

    // Implants state
    currentImplants,
    setCurrentImplants,
    implantTotalTeeth,
    setImplantTotalTeeth,
    firstDentalImplantAge,
    setFirstDentalImplantAge,
    lastDentalImplantAge,
    setLastDentalImplantAge,

    // Save state and helper functions
    isSaved,
    setIsSaved,
    prepareDentalDetails,

    // Calculations
    calculations,
  } = useDentalForm()

  // Use adjustment hooks for dental procedures
  const {
    adjustedAmalgamFillings,
    adjustedTotalAmalgamFillings,
    hasAmalgamAdjustments,
    hasTotalAmalgamAdjustments,
  } = useAmalgamAdjustments({
    dentalProcedures,
    isSaved,
    currentNumAmalgamFillings,
    amalgamTotalTeeth,
    dentalItems,
  })

  const {
    adjustedRootCanals,
    adjustedTotalRootCanals,
    hasRootCanalAdjustments,
    hasTotalRootCanalAdjustments,
  } = useRootCanalAdjustments({
    dentalProcedures,
    isSaved,
    currentRootCanals,
    rootCanalsTotal,
    dentalItems,
  })

  const {
    adjustedImplants,
    adjustedTotalImplants,
    hasImplantAdjustments,
    hasTotalImplantAdjustments,
  } = useImplantAdjustments({
    dentalProcedures,
    isSaved,
    currentImplants,
    implantTotalTeeth,
    dentalItems,
  })

  const { adjustedCavitations, hasCavitationAdjustments } =
    useCavitationAdjustments({
      dentalProcedures,
      isSaved,
      cavitations,
      cavitChecked,
      dentalItems,
    })

  // Use chart data hook
  const chartData = useChartData({
    isSaved,
    currentNumAmalgamFillings,
    adjustedAmalgamFillings,
    amalgamTotalTeeth,
    adjustedTotalAmalgamFillings,
    currentRootCanals,
    adjustedRootCanals,
    rootCanalsTotal,
    adjustedTotalRootCanals,
    currentImplants,
    adjustedImplants,
    implantTotalTeeth,
    adjustedTotalImplants,
  })

  // Use form submission hook
  const formValues = {
    currentNumAmalgamFillings,
    amalgamTotalTeeth,
    firstAmalgamAge,
    lastAmalgamAge,
    currentRootCanals,
    rootCanalsTotal,
    firstRootCanalAge,
    lastRootCanalAge,
    removedTeeth,
    firstToothExtractedAge,
    cavitChecked,
    cavitations,
    currentImplants,
    implantTotalTeeth,
    firstDentalImplantAge,
    lastDentalImplantAge,
  }

  const { handleSave, loading, allFieldsFilled } = useFormSubmission({
    uid,
    toast,
    formValues,
    isSaved,
    prepareDentalDetails,
    setIsSaved,
  })

  // Destructure calculations for clarity
  const {
    computedYearsExposed,
    amalgamError,
    computedExposureNumber,
    computedRootYearsExposed,
    rootCanalError,
    computedRootCanalExposure,
    computedCaviationExposure,
    computedImplantYearsExposed,
    computedImplantExposure,
  } = calculations

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSave()
      }}
    >
      <Box p={4}>
        <Stack spacing={4}>
          {purpleBubbleContent?.text && (
            <PurpleBubbleContent text={purpleBubbleContent.text} />
          )}

          {/* Dental Procedures History and Visualizations */}
          <DentalVisualizations
            isSaved={isSaved}
            loadingProcedures={loadingProcedures}
            dentalProcedures={dentalProcedures}
            dentalItems={dentalItems}
            amalgamChartData={chartData.amalgamChartData}
            amalgamChartOptions={chartData.amalgamChartOptions}
            rootCanalChartData={chartData.rootCanalChartData}
            rootCanalChartOptions={chartData.rootCanalChartOptions}
            implantChartData={chartData.implantChartData}
            implantChartOptions={chartData.implantChartOptions}
          />

          <Button
            type='submit'
            isDisabled={allFieldsFilled}
            isLoading={loading}
            size='lg'
            width='250px'
          >
            Save
          </Button>

          {/* Amalgams Section */}
          <AmalgamsSection
            currentNumAmalgamFillings={currentNumAmalgamFillings}
            setCurrentNumAmalgamFillings={setCurrentNumAmalgamFillings}
            amalgamTotalTeeth={amalgamTotalTeeth}
            setAmalgamTotalTeeth={setAmalgamTotalTeeth}
            firstAmalgamAge={firstAmalgamAge}
            setFirstAmalgamAge={setFirstAmalgamAge}
            lastAmalgamAge={lastAmalgamAge}
            setLastAmalgamAge={setLastAmalgamAge}
            computedYearsExposed={computedYearsExposed}
            computedExposureNumber={computedExposureNumber}
            amalgamError={amalgamError}
            isSaved={isSaved}
            adjustedAmalgamFillings={adjustedAmalgamFillings}
            adjustedTotalAmalgamFillings={adjustedTotalAmalgamFillings}
            hasAmalgamAdjustments={hasAmalgamAdjustments}
            hasTotalAmalgamAdjustments={hasTotalAmalgamAdjustments}
          />

          {/* Root Canals Section */}
          <RootCanalsSection
            currentRootCanals={currentRootCanals}
            setCurrentRootCanals={setCurrentRootCanals}
            rootCanalsTotal={rootCanalsTotal}
            setRootCanalsTotal={setRootCanalsTotal}
            firstRootCanalAge={firstRootCanalAge}
            setFirstRootCanalAge={setFirstRootCanalAge}
            lastRootCanalAge={lastRootCanalAge}
            setLastRootCanalAge={setLastRootCanalAge}
            computedRootYearsExposed={computedRootYearsExposed}
            computedRootCanalExposure={computedRootCanalExposure}
            rootCanalError={rootCanalError}
            isSaved={isSaved}
            adjustedRootCanals={adjustedRootCanals}
            adjustedTotalRootCanals={adjustedTotalRootCanals}
            hasRootCanalAdjustments={hasRootCanalAdjustments}
            hasTotalRootCanalAdjustments={hasTotalRootCanalAdjustments}
          />

          {/* Extractions Section */}
          <ExtractionsSection
            removedTeeth={removedTeeth}
            setRemovedTeeth={setRemovedTeeth}
            firstToothExtractedAge={firstToothExtractedAge}
            setFirstToothExtractedAge={setFirstToothExtractedAge}
            cavitChecked={cavitChecked}
            setCavitChecked={setCavitChecked}
            cavitations={cavitations}
            setCavitations={setCavitations}
            computedCaviationExposure={computedCaviationExposure}
            isSaved={isSaved}
            adjustedCavitations={adjustedCavitations}
            hasCavitationAdjustments={hasCavitationAdjustments}
          />

          {/* Implants Section */}
          <ImplantsSection
            currentImplants={currentImplants}
            setCurrentImplants={setCurrentImplants}
            implantTotalTeeth={implantTotalTeeth}
            setImplantTotalTeeth={setImplantTotalTeeth}
            firstDentalImplantAge={firstDentalImplantAge}
            setFirstDentalImplantAge={setFirstDentalImplantAge}
            lastDentalImplantAge={lastDentalImplantAge}
            setLastDentalImplantAge={setLastDentalImplantAge}
            computedImplantYearsExposed={computedImplantYearsExposed}
            computedImplantExposure={computedImplantExposure}
            isSaved={isSaved}
            adjustedImplants={adjustedImplants}
            adjustedTotalImplants={adjustedTotalImplants}
            hasImplantAdjustments={hasImplantAdjustments}
            hasTotalImplantAdjustments={hasTotalImplantAdjustments}
          />

          <Button
            mt={6}
            type='submit'
            isDisabled={allFieldsFilled}
            isLoading={loading}
            size='lg'
            width='150px'
          >
            Save
          </Button>
        </Stack>
      </Box>
    </form>
  )
}

export default DentalForm
